import type { Transaction, Auth } from 'oscar-typedef'
import { AptosPrepareData } from '../prepareData/AptosPrepareData'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type Account,
  type ITransactionExecutor,
  type EncUVD,
  type AptosSendExtraData,
} from '@/types'

export class AptosCoinTransactionExecutor implements ITransactionExecutor {
  private account: Account

  constructor(params: { account: Account }) {
    this.account = params.account
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<AptosPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.account.id,
          to: params.to,
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new AptosPrepareData({
      gasLimit: data.detail?.gasLimit ?? 0,
      gasPrice: {
        high: data.detail?.gasPrice.high ?? 0,
        medium: data.detail?.gasPrice.medium ?? 0,
        low: data.detail?.gasPrice.low ?? 0,
      },
      maxGasAmount: data.detail?.maxGasAmount ?? 0,
      publicKey: data.detail?.publicKey,
      sequenceNumber: data.detail?.sequenceNumber,
      isRegistered: true,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: AptosSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.account.walletId,
      accountId: this.account.id,
      pin: params.encUVD,
      txData: {
        txType: 'transfer',
        to: params.to,
        from: this.account.address,
        amount: params.amount,
        sequenceNumber: params.extraData.sequenceNumber,
        gasLimit: params.extraData.gasLimit,
        gasPrice: params.extraData.gasPrice,
        maxGasAmount: params.extraData.maxGasAmount,
        publicKey: params.extraData.publicKey,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    // @TODO Near broadcast
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.account.walletId,
        accountId: this.account.id,
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
